import React, { useState } from "react";
import { useGameIdFromRoute } from "../games/WithGameIdFromRoute";
import ListItemText from "@mui/material/ListItemText";
import ListItemButton from "@mui/material/ListItemButton";
import { Path } from "../Path";
import { useNavigate } from "react-router-dom";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import numeral from "numeral";
import Image from "@mui/icons-material/Image";
import { formatFullDate, GameAssetTypeLabels } from "@seabrookstudios/pitch2table-core";
import Download from "@mui/icons-material/Download";
import Link from "@mui/material/Link";
import IconButton from "@mui/material/IconButton";
import ListItem from "@mui/material/ListItem";
import Stack from "@mui/material/Stack";
import OpenInNew from "@mui/icons-material/OpenInNew";
import CheckCircle from "@mui/icons-material/CheckCircle";
import PictureAsPdf from "@mui/icons-material/PictureAsPdf";
import Article from "@mui/icons-material/Article";

const FileIcon = ({ mimetype }) => {
  if (mimetype.startsWith("image")) {
    return <Image />;
  }
  if (mimetype === "application/pdf") {
    return <PictureAsPdf />;
  }

  return <Article />;
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").GameAsset} props.item
 */
export const ListEntry = ({ item }) => {
  const gameId = useGameIdFromRoute();

  const navigate = useNavigate();

  const onClickHandler = () => {
    if (item.type === "Rules") {
      navigate(Path.game(gameId).rule(item.id));
      return;
    }
    if (item.type === "SellSheet") {
      navigate(Path.game(gameId).sellsheet(item.id));
      return;
    }

    navigate(Path.game(gameId).file(item.id));
  };

  return (
    <ListItemButton id={`topic-${item.id}`} alignItems="flex-start" onClick={onClickHandler}>
      <ListItemAvatar>
        <FileIcon mimetype={item.mimeType} />
      </ListItemAvatar>
      <ListItemText
        primary={
          <Typography variant="inherit" fontWeight="bold">
            {item.name || item.id}
          </Typography>
        }
        secondary={`Uploaded: ${formatFullDate(item.created)}`}
      />
      <ListItemAvatar>
        <Box paddingRight={1}>
          <Chip label={GameAssetTypeLabels[item.type]} size="small" />
        </Box>
      </ListItemAvatar>
      <ListItemAvatar>{numeral(item.bytes).format("0 b")}</ListItemAvatar>
    </ListItemButton>
  );
};

/**
 * @param {object} props
 * @param {string} props.mimeType determines icon
 * @param {string} props.name display name
 * @param {string} props.download what to call the file when downloaded
 * @param {import("@seabrookstudios/types").Url|string} props.assetUrl
 * @param {import("@seabrookstudios/types").Url|string} props.downloadUrl
 * @param {number} props.bytes
 */
export const PublicFile = ({ mimeType, name, download, assetUrl, downloadUrl, bytes }) => {
  const [showTick, setShowTick] = useState(false);

  const onDownload = () => {
    setShowTick(true);
  };

  return (
    <ListItem>
      <Box paddingRight={1}>
        <FileIcon mimetype={mimeType} />
      </Box>
      <ListItemText
        primary={
          <Typography variant="inherit" fontWeight="bold">
            {name || "unnamed file"}
          </Typography>
        }
        secondary={`${numeral(bytes).format("0 b")}`}
      />
      <Stack direction="row">
        <Box paddingRight={1}>
          <IconButton component={Link} href={`${assetUrl}`} target="_blank">
            <OpenInNew />
          </IconButton>
        </Box>
        <Box paddingRight={1}>
          <IconButton
            component={Link}
            href={`${downloadUrl}?filename=${download}`}
            download={`${download}`}
            onClick={onDownload}
          >
            {showTick ? <CheckCircle color="success" /> : <Download />}
          </IconButton>
        </Box>
      </Stack>
    </ListItem>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").PublicGame} props.game
 * @param {import("@seabrookstudios/pitch2table-core").PublicGameAsset} props.item
 */
export const PublicListEntry = ({ game, item }) => {
  return (
    <PublicFile
      assetUrl={item.assetUrl}
      downloadUrl={item.downloadUrl}
      mimeType={item.mimeType}
      name={item.name}
      download={`${game.name} - ${item.name}`}
      bytes={item.bytes}
    />
  );
};
